import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Config from "./../config.json";

export const weedApi = createApi({
  reducerPath: "weedApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${Config.IPM_SERVER_URL}/api/weeds/`,
    credentials: "include",
    headers: {
      "X-API-Key": Config.X_API_KEY,
    },
  }),
  endpoints: (builder) => ({
    getAllWeeds: builder.query({
      query: () => ({
        url: "getAll",
        params: {},
        method: "GET",
      }),
      providesTags: (result) => [
        { type: "Weed", id: "List" },
        ...(result ?? []).map((Weed) => ({ type: "Weed", id: Weed.id })),
      ],
    }),
    createWeed: builder.mutation({
      query: (disease) => ({
        url: "create",
        body: disease,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Weed", id: "List" },
        { type: "Weed", id: (id) => id },
      ],
    }),
    updateWeed: builder.mutation({
      query: (weed) => ({
        url: `updateWeed`,
        body: weed,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "Weed", id: "List" },
        { type: "Weed", id: (id) => id },
      ],
    }),
    onError: (error) => {
      console.error("An error occurred in the API:", error);
      // Handle the error here
    },
    deleteWeed: builder.mutation({
      query: (id) => ({
        url: `deleteWeed/${id}`,
        body: id,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Weed", id: "List" },
        { type: "Weed", id: (id) => id },
      ],
    }),
    deleteUploadedImage: builder.mutation({
      query: (id) => ({
        url: `deleteUploadedImage/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Weed", id: "List" },
        { type: "Weed", id: (id) => id },
      ],
    }),

    /// WeedInfoDescription
    createWeedInfoDescription: builder.mutation({
      query: (formData) => {
        return {
          url: "/createDescription",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [
        { type: "WeedInfoDescription", id: "List" },
        { type: "WeedInfoDescription", id: (id) => id },
      ],
    }),

    updateWeedInfoDescription: builder.mutation({
      query: (data) => ({
        url: `updateDescription`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "WeedInfoDescription", id: "List" },
        { type: "WeedInfoDescription", id: (id) => id },
      ],
    }),
    deleteWeedInfoDescription: builder.mutation({
      query: (id) => ({
        url: `deleteDescription/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "WeedInfoDescription", id: "List" },
        { type: "WeedInfoDescription", id: (id) => id },
      ],
    }),
    onError: (error) => {
      console.error("An error occurred in the API:", error);
      // Handle the error here
    },
    deleteUploadedImageInWeedInfoDescription: builder.mutation({
      query: (id) => ({
        url: `deleteUploadedImage/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "WeedInfoDescription", id: "List" },
        { type: "WeedInfoDescription", id: (id) => id },
      ],
    }),
    getWeedInfoDescription: builder.query({
      query: (Id) => ({
        url: `getDescription/${Id}`,
        params: {},
        method: "GET",
      }),
      providesTags: (result) => [
        { type: "WeedInfoDescription", id: "List" },
        ...(result ?? []).map((WeedInfoDescription) => ({
          type: "WeedInfoDescription",
          id: WeedInfoDescription.id,
        })),
      ],
    }),

    // New Description
    getWeedDescription: builder.query({
      query: (dto) => ({
        url: `get-description/${dto.Id}/${dto.Type}`,
        params: {},
        method: "GET",
      }),
      providesTags: (result, error, dto) => [
        { type: "WeedDescriptionData", id: dto.Id },
      ],
    }),
    updateWeedDescription: builder.mutation({
      query: (dto) => ({
        url: `updated-description`,
        body: dto,
        method: "PUT",
      }),
      // invalidatesTags: [{ type: 'WeedDescription', id: 'List' },
      // { type: 'WeedDescriptionData', id: (id) => id }],
    }),

    uploadWeedDescriptionImage: builder.mutation({
      query: (dto) => {
        return {
          url: "/upload-image",
          method: "POST",
          body: dto,
        };
      },
      invalidatesTags: [
        { type: "WeedDescriptionData", id: "List" },
        { type: "WeedDescriptionData", id: (id) => id },
      ],
    }),

    deleteDescriptionImage: builder.mutation({
      query: (imageName) => ({
        url: `delete-uploaded-image/${imageName}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "WeedDescriptionData", id: "List" },
        { type: "WeedDescriptionData", id: (id) => id },
      ],
    }),

    // Weed Images
    getWeedImages: builder.query({
      query: (weedId) => ({
        url: `get-weed-images/${weedId}`,
        params: {},
        method: "GET",
      }),
      providesTags: (result) => [
        { type: "WeedImage", id: "List" },
        ...result?.map((WeedImage) => ({
          type: "WeedImage",
          id: WeedImage.id,
        })),
      ],
    }),
    updateImageOrder: builder.mutation({
      query: (data) => {
        return {
          url: `/update-image-order`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: [
        { type: "WeedImage", id: "List" },
        { type: "WeedImage", id: (id) => id },
      ],
    }),
    createWeedImage: builder.mutation({
      query: (entity) => {
        return {
          url: `/upload-weed-image/${entity.Id}`,
          method: "POST",
          body: entity.imageFormData,
        };
      },
      invalidatesTags: [
        { type: "WeedImage", id: "List" },
        { type: "WeedImage", id: (id) => id },
      ],
    }),
    deleteWeedImage: builder.mutation({
      query: (image) => ({
        url: `/delete-weed-image`,
        body: image,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "WeedImage", id: "List" },
        { type: "WeedImage", id: (id) => id },
      ],
    }),
  }),
});

export const {
  useGetWeedDescriptionQuery,
  useUpdateWeedDescriptionMutation,
  useDeleteDescriptionImageMutation,
  useUploadWeedDescriptionImageMutation,
  useGetAllWeedsQuery,
  useDeleteUploadedImageInWeedInfoDescriptionMutation,
  useDeleteUploadedImageMutation,
  useDeleteWeedInfoDescriptionMutation,
  useUpdateWeedInfoDescriptionMutation,
  useCreateWeedMutation,
  useUpdateWeedMutation,
  useDeleteWeedMutation,
  useGetWeedInfoDescriptionQuery,
  useCreateWeedInfoDescriptionMutation,
  useGetWeedImagesQuery,
  useCreateWeedImageMutation,
  useDeleteWeedImageMutation,
  useUpdateImageOrderMutation
} = weedApi;
